/**
 * Temu bg.glo.product.search（货品生命周期）→ 发布记录状态。
 * 字段以平台文档为准：skcList.selectStatus / skuList.applyJitStatus。
 */

import {
  PUBLISH_RECORD_STATUS,
  collectRecordSkuIds,
} from '@/temu-platform/utils/temuPublishRecordStatus';

/** 选品状态 → 中文（未收录的码显示「选品状态 n」） */
const SELECT_STATUS_LABEL = {
  0: '待选品',
  1: '选品中',
  2: '已选中',
  3: '选品未通过',
  4: '已下架',
  5: '已淘汰',
  6: '待定价',
  7: '已下架',
  8: '已定价',
  10: '待完善信息',
  11: '处理中',
  12: '资质下架',
  13: '已清退',
};

const APPLY_JIT_LABEL = {
  1: '可申请 JIT',
  3: '不可申请 JIT',
};

export function selectStatusLabel(code) {
  const n = Number(code);
  if (!Number.isFinite(n)) return '';
  return SELECT_STATUS_LABEL[n] || `选品状态 ${n}`;
}

export function applyJitStatusLabel(code) {
  const n = Number(code);
  if (!Number.isFinite(n)) return '';
  return APPLY_JIT_LABEL[n] || `JIT ${n}`;
}

/** selectStatus → 发布记录四档 */
export function selectStatusToPublishLabel(code) {
  const n = Number(code);
  if (!Number.isFinite(n)) return null;
  if (n === 2 || n === 8) return PUBLISH_RECORD_STATUS.APPROVED;
  if (n === 3 || n === 4 || n === 5 || n === 7 || n === 12 || n === 13) {
    return PUBLISH_RECORD_STATUS.REJECTED;
  }
  if (n === 6 || n === 10) return PUBLISH_RECORD_STATUS.PENDING_MERCHANT;
  if (n === 0 || n === 1 || n === 11) return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
}

function pushId(set, raw) {
  const n = Number(raw);
  if (Number.isSafeInteger(n) && n > 0) set.add(String(n));
  const t = String(raw ?? '').trim();
  if (t && t !== '0') set.add(t);
}

/** 从发布记录列表收集可查的 productSkuId */
export function collectPublishRecordSkuIds(records = []) {
  const ids = new Set();
  (Array.isArray(records) ? records : []).forEach((row) => {
    collectRecordSkuIds(row).forEach((id) => pushId(ids, id));
  });
  return [...ids].map((id) => Number(id)).filter((n) => Number.isSafeInteger(n) && n > 0);
}

/**
 * 把 lifecycle/search 响应建成索引。
 * @returns {{ bySkcId: Map<string, object>, bySkuId: Map<string, object>, byProductId: Map<string, object> }}
 */
export function buildLifecycleIndex(items = []) {
  const bySkcId = new Map();
  const bySkuId = new Map();
  const byProductId = new Map();
  (Array.isArray(items) ? items : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const productId = row.productId != null ? String(row.productId) : '';
    const skcList = Array.isArray(row.skcList) ? row.skcList : [];
    const skuList = Array.isArray(row.skuList) ? row.skuList : [];
    const productHit = {
      productId,
      skcList,
      skuList,
    };
    if (productId) byProductId.set(productId, productHit);
    skcList.forEach((skc) => {
      const skcId = skc?.skcId != null ? String(skc.skcId) : '';
      if (!skcId) return;
      bySkcId.set(skcId, {
        ...skc,
        productId,
        skuList,
      });
    });
    skuList.forEach((sku) => {
      const skuId = sku?.skuId != null ? String(sku.skuId) : '';
      if (!skuId) return;
      // 挂上同货品下第一条 skc 的选品态，便于按 sku 回填
      const firstSkc = skcList[0] || {};
      bySkuId.set(skuId, {
        ...sku,
        productId,
        selectStatus: firstSkc.selectStatus,
        skcId: firstSkc.skcId,
      });
    });
  });
  return { bySkcId, bySkuId, byProductId };
}

function resolveSkcLifecycle(skc, record, index) {
  if (!index) return null;
  const skcId = String(skc?.productSkcId || skc?.skcId || skc?.skcName || '').trim();
  if (skcId && index.bySkcId.has(skcId)) return index.bySkcId.get(skcId);
  const skuIds = collectRecordSkuIds(skc);
  for (const id of skuIds) {
    if (index.bySkuId.has(id)) return index.bySkuId.get(id);
  }
  const productId = String(record?.platformSpuName || record?.productId || '').trim();
  if (productId && index.byProductId.has(productId)) {
    return index.byProductId.get(productId)?.skcList?.[0]
      ? {
        ...index.byProductId.get(productId).skcList[0],
        productId,
        skuList: index.byProductId.get(productId).skuList,
      }
      : null;
  }
  return null;
}

function aggregatePublishLabels(labels) {
  const list = (Array.isArray(labels) ? labels : []).filter(Boolean);
  if (!list.length) return null;
  if (list.includes(PUBLISH_RECORD_STATUS.PENDING_MERCHANT)) {
    return PUBLISH_RECORD_STATUS.PENDING_MERCHANT;
  }
  if (list.includes(PUBLISH_RECORD_STATUS.REJECTED)) {
    return PUBLISH_RECORD_STATUS.REJECTED;
  }
  if (list.every((s) => s === PUBLISH_RECORD_STATUS.APPROVED)) {
    return PUBLISH_RECORD_STATUS.APPROVED;
  }
  if (list.includes(PUBLISH_RECORD_STATUS.APPROVED)) {
    return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  }
  return PUBLISH_RECORD_STATUS.PENDING_REVIEW;
}

/**
 * 用生命周期覆盖仍为「待审核」的记录（核价已给出更明确态时保留核价结果）。
 */
export function applyLifecycleToPublishRecord(record, lifecycleIndex) {
  if (!record || typeof record !== 'object' || !lifecycleIndex) return record;
  const skcItems = Array.isArray(record.skcItems) ? record.skcItems : [];
  const nextSkc = skcItems.map((skc) => {
    const hit = resolveSkcLifecycle(skc, record, lifecycleIndex);
    if (!hit) return skc;
    const selectStatus = hit.selectStatus;
    const lifecycleLabel = selectStatusToPublishLabel(selectStatus);
    const current = String(skc.statusLabel || skc.status || '').trim();
    const keepReview = current
      && current !== PUBLISH_RECORD_STATUS.PENDING_REVIEW
      && current !== '';
    const statusLabel = keepReview ? current : (lifecycleLabel || current || PUBLISH_RECORD_STATUS.PENDING_REVIEW);
    const skuIds = collectRecordSkuIds(skc);
    let applyJitStatus;
    let suggestCloseJit;
    const skuList = Array.isArray(hit.skuList) ? hit.skuList : [];
    const lifeSkuIds = skuList
      .map((sku) => Number(sku?.skuId ?? sku?.productSkuId))
      .filter((n) => Number.isSafeInteger(n) && n > 0);
    for (const sku of skuList) {
      const id = String(sku?.skuId ?? '');
      if (id && skuIds.has(id)) {
        applyJitStatus = sku.applyJitStatus;
        suggestCloseJit = sku.suggestCloseJit;
        break;
      }
    }
    if (applyJitStatus == null && hit.applyJitStatus != null) {
      applyJitStatus = hit.applyJitStatus;
      suggestCloseJit = hit.suggestCloseJit;
    }
    const existingSkuIds = (Array.isArray(skc.productSkuIds) ? skc.productSkuIds : [])
      .map((v) => Number(v))
      .filter((n) => Number.isSafeInteger(n) && n > 0);
    const nextSkuIds = existingSkuIds.length ? existingSkuIds : lifeSkuIds;
    return {
      ...skc,
      productSkuIds: nextSkuIds,
      merchantSkus: nextSkuIds.length
        ? nextSkuIds.map((id) => ({ productSkuId: id }))
        : (skc.merchantSkus || []),
      status: statusLabel,
      statusLabel,
      selectStatus,
      selectStatusLabel: selectStatusLabel(selectStatus),
      applyJitStatus,
      applyJitStatusLabel: applyJitStatusLabel(applyJitStatus),
      suggestCloseJit: Boolean(suggestCloseJit),
      lifecycleSynced: true,
    };
  });

  let label = String(record.statusLabel || record.status || '').trim();
  const keepRecordReview = label
    && label !== PUBLISH_RECORD_STATUS.PENDING_REVIEW;
  if (!keepRecordReview) {
    const fromSkc = aggregatePublishLabels(nextSkc.map((s) => s.statusLabel));
    if (fromSkc) label = fromSkc;
    else if (!skcItems.length) {
      const productId = String(record.platformSpuName || '').trim();
      const hit = productId ? lifecycleIndex.byProductId.get(productId) : null;
      const selectStatus = hit?.skcList?.[0]?.selectStatus;
      const mapped = selectStatusToPublishLabel(selectStatus);
      if (mapped) label = mapped;
    }
  }

  return {
    ...record,
    skcItems: nextSkc,
    status: label || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    statusLabel: label || PUBLISH_RECORD_STATUS.PENDING_REVIEW,
    lifecycleSynced: nextSkc.some((s) => s.lifecycleSynced) || Boolean(label),
  };
}

export function applyLifecycleToPublishRecords(records = [], lifecycleItems = []) {
  const index = buildLifecycleIndex(lifecycleItems);
  if (!index.bySkcId.size && !index.bySkuId.size && !index.byProductId.size) {
    return Array.isArray(records) ? records : [];
  }
  return (Array.isArray(records) ? records : []).map((row) => (
    applyLifecycleToPublishRecord(row, index)
  ));
}
